'use client'

import { useEffect, useState } from 'react'
import { Button, Group, VisuallyHidden } from '@mantine/core'
import { CircleUser } from 'lucide-react'
import { useUserContext } from '@/contexts/UserContext'
import { loginPath } from '@/lib/namedRoutes'
import { MercyLogo } from './MercyLogo'
import classes from './NavBar.module.css'
import { appPath } from '@/lib/getPublicAppUrl'
import mercyContent from '@/lib/locales/mercy/en.json'

export interface MercySiteHeaderProps {
    /** Signed-in patients see their first name instead of Sign in. */
    signedIn: boolean
    /** Patient first name. Empty while the profile is still loading. */
    firstName?: string
}

/**
 * Mercy header: centered wordmark, with Sign in or the patient name on the right.
 */
export function MercySiteHeader({ signedIn, firstName }: MercySiteHeaderProps) {
    const accountName = firstName?.trim()

    return (
        <header className={classes.header} data-testid="mercy-navbar">
            <div className={classes.logo}>
                <MercyLogo />
            </div>
            <div className={classes.accountSlot}>
                {signedIn ? (
                    <Group gap="8px" className={classes.accountChip} data-testid="mercy-header-account">
                        <CircleUser size={20} strokeWidth={1.75} aria-hidden />
                        {accountName ? (
                            <span
                                className={classes.accountName}
                                data-testid="mercy-header-account-name"
                                data-dd-privacy="mask"
                            >
                                {accountName}
                            </span>
                        ) : (
                            <VisuallyHidden>Account</VisuallyHidden>
                        )}
                    </Group>
                ) : (
                    <Button
                        component="a"
                        size="sm"
                        variant="outline"
                        href={appPath(loginPath)}
                        className={classes.accountChip}
                        data-testid="mercy-header-sign-in"
                        data-dd-privacy="allow"
                    >
                        <Group gap="8px">
                            <CircleUser size={20} strokeWidth={1.75} aria-hidden />
                            {mercyContent.navBar.signIn}
                        </Group>
                    </Button>
                )}
            </div>
        </header>
    )
}

interface NavBarProps {
    /**
     * True when the request already has session cookies.
     * Keeps the account control in place while the patient profile loads.
     */
    initiallyLoggedIn?: boolean
}

/** Mercy app header. Reads the session so the right-side control can show the patient name. */
export default function NavBar({ initiallyLoggedIn = false }: NavBarProps) {
    const { fetchUser, user } = useUserContext()
    const [hasResolvedUser, setHasResolvedUser] = useState(!initiallyLoggedIn)

    // Session cookies keep the account control in place until the profile
    // request settles. fetchUser is throttled and can return undefined.
    useEffect(() => {
        if (user || !initiallyLoggedIn) {
            setHasResolvedUser(true)
            return
        }

        let active = true
        const resolveUser = async () => {
            try {
                await fetchUser()
            } catch {
                // A failed profile request ends loading and the header falls back to Sign in.
            } finally {
                if (active) setHasResolvedUser(true)
            }
        }
        void resolveUser()

        return () => {
            active = false
        }
    }, [fetchUser, initiallyLoggedIn, user])

    const signedIn = Boolean(user) || (!hasResolvedUser && initiallyLoggedIn)

    return <MercySiteHeader signedIn={signedIn} firstName={user?.firstName ?? undefined} />
}
